// What Today's "+ Add task" sheet offers: suggestions with their reasons, search matches, and the
// user's goals in importance or urgency order. Pure: the sheet only renders these.
import { overdueTasks, dueSoonTasks, mostImportantGoals, radarTaskSort } from "../radar/signals";
import { sortGoalsForView, tasksForGoal } from "../tasks/goalOrder";
import { dueWords } from "../../shared/deadlines";

export const PHASE_LABELS = { morning: "Morning", afternoon: "Afternoon", evening: "Evening" };
export const PHASE_IDS = Object.keys(PHASE_LABELS);
// How a sentence names the part of the day: "Worth a look tonight".
export const PHASE_WHEN = { morning: "this morning", afternoon: "this afternoon", evening: "tonight" };
export const SUGGESTION_COUNT = 3;

// "13:30" from Settings → minutes since midnight.
export const minutes = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + (m || 0); };
// The part of the day it is now, by the user's settings. Before the afternoon starts it is morning, so a
// task added in the small hours goes to the morning.
export const phaseNow = (settings, now = new Date()) => {
  const mins = now.getHours() * 60 + now.getMinutes();
  if (mins >= minutes(settings?.eveningStart || "17:00")) return "evening";
  if (mins >= minutes(settings?.afternoonStart || "12:00")) return "afternoon";
  return "morning";
};

// Tasks that can be added to this part of the day: not done, and not already in it. A task planned
// for another part of the day is included, so it can be moved.
export const addableTasks = (tasks, phaseId) => (tasks || []).filter(t => !t.done && t.todayPhase !== phaseId);

// Up to SUGGESTION_COUNT tasks worth a look, each with its reason: overdue first, then due soon, then the
// next task of each goal in importance order. Ranks are the same numbers the goal list shows.
export const suggestions = (tasks, goals, phaseId, dueSoonWindow = 7) => {
  const open = addableTasks(tasks, phaseId);
  const activeGoals = (goals || []).filter(g => g.status === "active");
  const picked = [];
  const add = (task, why, tone) => {
    if (picked.length < SUGGESTION_COUNT && !picked.some(p => p.task.id === task.id)) picked.push({ task, why, tone });
  };
  overdueTasks(open).forEach(t => add(t, dueWords(t.deadline).text, "overdue"));
  dueSoonTasks(open, dueSoonWindow).forEach(t => add(t, dueWords(t.deadline).text, "soon"));
  mostImportantGoals(activeGoals).forEach(({ goal, i }) => {
    const next = tasksForGoal(open, goal.id)[0];
    if (next) add(next, i === 1 ? "Your most important goal" : `Your #${i} most important goal`, "goal");
  });
  return picked;
};

// Search across every addable task, soonest deadline first.
export const matchTasks = (tasks, phaseId, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return addableTasks(tasks, phaseId).filter(t => (t.text || "").toLowerCase().includes(q)).sort(radarTaskSort);
};

// Active goals in the chosen order, each with its rank in that order and its addable tasks. A goal with
// nothing to add is left out, but keeps its rank so the numbers match the Goals page.
export const goalsWithTasks = (tasks, goals, phaseId, view) => {
  const open = addableTasks(tasks, phaseId);
  return sortGoalsForView(goals || [], view)
    .map((goal, index) => ({ goal, rank: index + 1, tasks: tasksForGoal(open, goal.id) }))
    .filter(g => g.tasks.length > 0);
};

// Addable tasks with no active goal.
export const tasksWithoutGoal = (tasks, goals, phaseId) => {
  const active = new Set((goals || []).filter(g => g.status === "active").map(g => g.id));
  return addableTasks(tasks, phaseId).filter(t => !t.parentGoalId || !active.has(t.parentGoalId)).sort(radarTaskSort);
};

// Putting a task in a part of the day. A task moved from another part loses the time it had there; a
// plain add leaves its time alone. `before` is what Undo writes back.
export const phaseChange = (task, phaseId) => {
  const moved = !!task.todayPhase && task.todayPhase !== phaseId;
  return {
    update: moved ? { todayPhase: phaseId, todayTime: null } : { todayPhase: phaseId },
    before: { todayPhase: task.todayPhase || null, todayTime: task.todayTime || null },
    message: `${moved ? "Moved" : "Added"} to ${PHASE_LABELS[phaseId]}`,
  };
};
